import styles from './roleDashboard.module.css'
import { useEffect, useMemo, useState } from 'react'
import { Layout } from 'react-grid-layout'
import { Button, MenuItem, ModalId, useUIActions, useUIHooks } from '../../../../features/ui'
import { Branch, useManagerState } from '../../../manager'
import { useAdministrationState } from '../../../administration'
import { DashboardWidgetPreference, UserRole } from '../../../system'
import { useAdminSalesData } from '../../hooks/admin/useAdminSalesData'
import { useAdminOpsData } from '../../hooks/admin/useAdminOpsData'
import { useManagerFinanceData } from '../../hooks/admin/useManagerFinanceData'
import { useSalesKpis } from '../../hooks/admin/useSalesKpis'
import { useWarehouseData } from '../../hooks/warehouse/useWarehouseData'
import { useLogisticsData } from '../../hooks/logistics/useLogisticsData'
import { usePurchasesData } from '../../hooks/purchases/usePurchasesData'
import { getDefaultPreferences, useDashboardPreferences } from '../../hooks/useDashboardPreferences'
import { DashboardDataSource, DashboardScope, RoleDashboardWidget } from '../../types'
import { DashboardGrid } from '../dashboard-grid/dashboardGrid'
import { DashboardConfigModal } from '../dashboard-config-modal/dashboardConfigModal'
import { DashboardData, getDashboardWidgets } from '../dashboard-widgets/dashboardWidgets'
import { getDashboardRoles } from '../dashboard-widgets/dashboardRoles'

// Screens of the user menu (a card that links to a screen outside of it isn't offered)
const getMenuPaths = (items: MenuItem[]): string[] =>
    items.flatMap(item => [...(item.path ? [item.path] : []), ...getMenuPaths(item.children || [])])

type Props = {
    role: UserRole
    editing: boolean
    onFinishEditing: () => void
}

// Home of a role: its cards, that the user can move, resize, hide and add ("Personalizar" in the page header)
export const RoleDashboard = (props: Props) => {
    const { role, editing, onFinishEditing } = props
    const roleConfig = getDashboardRoles()[role]!
    const { scope } = roleConfig

    const { branches, currentBranch } = useManagerState()
    const { currentEmployee } = useAdministrationState()
    const { getMenu } = useUIHooks()
    const { openModal } = useUIActions()

    const roleWidgets = useMemo((): RoleDashboardWidget<DashboardData>[] => {
        const menuPaths = getMenuPaths(getMenu())
        const dashboardWidgets = getDashboardWidgets()
        const isAllowed = (id: string) => {
            const widget = dashboardWidgets[id]
            return !!widget && (!widget.linkPath || menuPaths.includes(widget.linkPath))
        }
        const defaults = roleConfig.defaults
            .map(entry => typeof entry === 'string' ? { id: entry } : entry)
            .filter(({ id }) => isAllowed(id))
            .map(({ id, ...place }) => ({ widget: dashboardWidgets[id], visible: true, ...place }))
        const available = roleConfig.available
            .filter(isAllowed)
            .map(id => ({ widget: dashboardWidgets[id], visible: false }))
        return [...defaults, ...available]
    }, [role])
    const widgets = useMemo(() => roleWidgets.map(({ widget }) => widget), [roleWidgets])

    const { preferences, savePreferences } = useDashboardPreferences(roleWidgets)

    // While editing, the changes are made on a draft that is only saved with "Guardar"
    const [draft, setDraft] = useState<DashboardWidgetPreference[]>(preferences)
    useEffect(() => {
        if (editing) {
            setDraft(preferences)
        }
    }, [editing])
    const shownPreferences = editing ? draft : preferences

    // Only the data of the visible cards is loaded
    const neededSources = useMemo(() => {
        const visibleIds = shownPreferences.filter(preference => preference.visible).map(preference => preference.id)
        return new Set(widgets
            .filter(widget => visibleIds.includes(widget.id))
            .flatMap(widget => widget.sources))
    }, [shownPreferences, widgets])

    const salesState = useAdminSalesData(neededSources.has(DashboardDataSource.SALES))
    const opsState = useAdminOpsData(neededSources.has(DashboardDataSource.OPERATIONS))
    const financeState = useManagerFinanceData(neededSources.has(DashboardDataSource.FINANCE))
    const warehouseState = useWarehouseData(neededSources.has(DashboardDataSource.WAREHOUSE))
    const logisticsState = useLogisticsData(neededSources.has(DashboardDataSource.LOGISTICS))
    const purchasesState = usePurchasesData(neededSources.has(DashboardDataSource.PURCHASES))

    // The data is limited to the role scope: the seller's own operations or the current branch ones
    const branchId = currentBranch?.id
    const sellerId = currentEmployee?.id
    const scopedSales = useMemo(() => {
        const data = salesState.data
        if (!data) return data
        if (scope === DashboardScope.MINE && sellerId) {
            return {
                ...data,
                sales: data.sales.filter(sale => sale.sellerId === sellerId),
                hoards: data.hoards.filter(hoard => hoard.sellerId === sellerId),
                budgets: data.budgets.filter(budget => budget.sellerId === sellerId)
            }
        }
        if (scope === DashboardScope.BRANCH && branchId) {
            return {
                ...data,
                sales: data.sales.filter(sale => sale.branchId === branchId),
                hoards: data.hoards.filter(hoard => hoard.branchId === branchId)
            }
        }
        return data
    }, [salesState.data, scope, sellerId, branchId])
    const scopedOperations = useMemo(() => {
        const data = opsState.data
        if (!data || scope !== DashboardScope.BRANCH || !branchId) return data
        return {
            pendingShipments: data.pendingShipments.filter(deliveryOrder => deliveryOrder.branchId === branchId),
            pendingReceptions: data.pendingReceptions.filter(purchase => purchase.deliveryBranchId === branchId)
        }
    }, [opsState.data, scope, branchId])
    const scopedPurchases = useMemo(() => {
        const data = purchasesState.data
        if (!data || scope !== DashboardScope.BRANCH || !branchId) return data
        return {
            ...data,
            pendingPurchases: data.pendingPurchases.filter(purchase => purchase.deliveryBranchId === branchId),
            overduePurchases: data.overduePurchases.filter(purchase => purchase.deliveryBranchId === branchId)
        }
    }, [purchasesState.data, scope, branchId])

    const salesKpis = useSalesKpis(
        scopedSales?.sales || [],
        scopedSales?.hoards || [],
        scopedSales?.budgets || [],
        scopedSales?.customers || []
    )

    const data: DashboardData = {
        scope,
        branchId,
        sales: { ...salesState, data: scopedSales },
        operations: { ...opsState, data: scopedOperations },
        finance: financeState,
        warehouse: warehouseState,
        logistics: logisticsState,
        purchases: { ...purchasesState, data: scopedPurchases },
        salesKpis,
        branches: branches as Branch[]
    }

    const onLayoutChange = (layout: Layout[]) => setDraft(current => current.map(preference => {
        const item = layout.find(layoutItem => layoutItem.i === preference.id)
        return item ? { ...preference, x: item.x, y: item.y, w: item.w, h: item.h } : preference
    }))

    const onHideWidget = (id: string) =>
        setDraft(current => current.map(preference => preference.id === id ? { ...preference, visible: false } : preference))

    const onReset = () => setDraft(getDefaultPreferences(roleWidgets))

    const onSave = async () => {
        await savePreferences(draft)
        onFinishEditing()
    }

    return (
        <>
            {editing && (
                <div className={styles.editBar}>
                    <span className={styles.editHint}>
                        <i className="bi bi-info-circle" /> Arrastrá las tarjetas para moverlas y estirá la esquina inferior derecha para cambiar su tamaño.
                    </span>
                    <div className={styles.editActions}>
                        <Button label="Agregar tarjetas" icon="plus-lg" kind="tertiary" small onClick={() => openModal(ModalId.DASHBOARD_CONFIG)} />
                        <Button label="Restablecer" icon="arrow-counterclockwise" kind="tertiary" small onClick={onReset} />
                        <Button label="Cancelar" kind="secondary" small onClick={onFinishEditing} />
                        <Button label="Guardar" icon="check-lg" small onClick={onSave} />
                    </div>
                </div>
            )}
            <DashboardGrid
                widgets={widgets}
                preferences={shownPreferences}
                data={data}
                editing={editing}
                onLayoutChange={onLayoutChange}
                onHideWidget={onHideWidget}
            />
            <DashboardConfigModal widgets={widgets} preferences={draft} onApply={setDraft} />
        </>
    )
}
